<script setup>
import { watch, getCurrentInstance, computed, nextTick } from "vue";
import { verLotesSubasta } from "@/utils/subastaRedirections";
import { getFormattedDate, getFormattedTime } from '@/utils/helpers'
import { getBadgeClass, getBadgeText } from "@/utils/badge";
import { useUsuarioStore } from "@/store/usuarioStore";
import { storeToRefs } from "pinia";

const props = defineProps({
  carteleraStore: Object,
  listadosEntidadesStore: Object
});

const usuarioStore = useUsuarioStore();
const { getUsuario } = storeToRefs(usuarioStore);

const subastas = computed(() => props.carteleraStore?.subastas);

// Muestra subastas privadas solo si el email del usuario logueado está en usuarios_permitidos
const subastasVisibles = computed(() => {
  const data = subastas.value?.data ?? [];
  const userEmail = getUsuario.value?.email?.toLowerCase();

  return data.filter((subasta) => {
    if (!subasta.privada) return true;
    if (!userEmail) return false;

    return (subasta.usuarios_permitidos ?? []).some(
      (u) => u.email?.toLowerCase() === userEmail
    );
  });
});

const { appContext } = getCurrentInstance();
const storage_path = appContext.config.globalProperties.storage_path;

watch(
  () => subastas.value.current_page,
  (newPage) => {
    props.carteleraStore.updatePaginationSubastas("current_page", newPage);
  }
);

watch(
  () => subastas.value.per_page,
  (newPerPage) => {
    props.carteleraStore.updatePaginationSubastas("per_page", newPerPage);
  }
);

const goToPage = async (page) => {
  if (page > 0 && page <= subastas.value.last_page) {
    subastas.value.current_page = page;
    await nextTick();
    document
      .getElementById("carteleraSubasta")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};
</script>

<template>
  <div class="row g-4" id="carteleraSubasta">
    <div v-if="(!subastasVisibles || subastasVisibles.length === 0)" class="text-center py-5">
        <img src="@/assets/img/illustrations/sin_resultados.png" alt="Sin lotes" style="max-width: 280px;" class="mb-4">
        <h4 class="text-muted">No hay subastas disponibles.</h4>
        <p class="text-muted fs-6">No hay subastas disponibles en este momento. <br>Te avisaremos cuando haya novedades.</p>
    </div>
    <a class="col-sm-6 col-lg-6" v-for="(subasta) in subastasVisibles || []" :key="subasta.id"
      @click="verLotesSubasta(subasta.id)" href="javascript:;">
      <div class="card shadow-none h-100 hover-zoom" style=" height: 333px;">
        <div class="image-container position-relative">
          <span class="position-absolute top-0 start-0 m-3 px-2 py-1 rounded-2 text-white fs-11 uniform-badge" :class="getBadgeClass(subasta.estado_subasta.slug)">
              {{ getBadgeText(subasta.estado_subasta.nombre) }}
          </span>
          <img v-if="subasta.imagen" :src="`${storage_path}/${subasta.imagen.hash}.${subasta.imagen.extension}`"
            class="card-img-top rounded-top-8" alt="Imagen de la subasta" />
          <div v-else class="no-image-placeholder text-subtitulos-secondary">
            <div class="placeholder-content">
              <i class="fa fa-gavel fa-3x mb-2"></i>
              <p class="mb-0">ID: {{ subasta.external_id || "0000" }}</p>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div class="badges-soft d-flex flex-wrap align-items-center gap-1 mb-2">
            <span class="text-white badge bg-badge-soft-green d-inline-flex align-items-center fs-11 uniform-badge">
              <img class="me-1" src="@/assets/img/icons/click-mini.svg" alt="">
              {{ subasta.modalidad_subasta.nombre == 'Subasta' 
              ? 'Cronometrada' 
              : subasta.modalidad_subasta.nombre }}
            </span>
            <span class="badge bg-soft-orange text-soft-organge fs-11 uniform-badge" v-if="subasta.privada">
              Subasta interna
            </span>
          </div>
          <h6 class="card-title fs-5">{{ subasta.titulo }}</h6>
        </div>
        <div class="card-footer pt-0 d-flex justify-content-between align-items-baseline text-subtitulos-secondary">
          <div class="d-flex align-items-baseline">
            <i class="fa-regular fa-calendar fs-15 me-1 text-soft-grey-74"></i>
            <small class="fs-13">
              {{ getFormattedDate(subasta.fecha_hora_inicio) || 'A confirmar' }}
            </small>
          </div>
          <div class="d-flex align-items-center lh-1">
            <i class="fa-regular fa-clock fs-15 me-1 text-soft-grey-74 lh-1"></i>
            <small class="fs-13 lh-1">
              {{ getFormattedTime(subasta.fecha_hora_inicio) + ' hs' || 'A confirmar' }}
            </small>
          </div>
          <div class="d-flex align-items-baseline">
            <i class="fa-regular fa-folder fs-15 me-1 text-soft-grey-74"></i>
            <small class="fs-13">ID {{ subasta.external_id }}</small>
          </div>
        </div>
      </div>
    </a>
  </div>

  <!-- Paginador -->
<div class="d-flex justify-content-center">
  <div class="d-flex justify-content-between align-items-center mt-3 px-3">
    <nav class="mt-3 " v-if="subastas.last_page > 1">
      <div class="custom-pagination w-100">
        <a :href="'#carteleraSubasta'" @click.prevent="goToPage(subastas.current_page - 1)" class="page-nav"
          :class="{ disabled: subastas.current_page === 1 }">
          <i class="fa fa-chevron-left"></i> Anterior
        </a>

        <a v-for="page in subastas.last_page" :key="page" :href="'#carteleraSubasta'" class="page-number fw-bold text-white"
          :class="{ active: page === subastas.current_page }" @click.prevent="goToPage(page)">
          {{ page }}
        </a>

        <a :href="'#carteleraSubasta'" @click.prevent="goToPage(subastas.current_page + 1)" class="page-nav"
          :class="{ disabled: subastas.current_page === subastas.last_page }">
          Siguiente <i class="fa fa-chevron-right"></i>
        </a>
      </div>
    </nav>
  </div>
</div>
</template>
<style scoped>
.image-container {
  background-color: #f8f9fa;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
  width: 100%;
  overflow: hidden;
}

.card-img-top {
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
}

.no-image-placeholder i {
  font-size: 3rem;
}

.no-image-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.pagination .page-link {
  color: #007bff;
}


.uniform-badge {
  line-height: 1;        
  height: 24px;          
  display: inline-flex;
  align-items: center;
  padding-top: 2px;       
  padding-bottom: 2px;
}
.uniform-badge img {
  height: 12px;         
  vertical-align: middle;
}
</style>